import type { RefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useScrollToContent } from '#/hooks/useScrollToContent';
import { useSectionScrollSpy } from '#/hooks/useSectionScrollSpy';

/**
 * Wires the detail-page section navigator: jump-link scrolling, scroll-spy
 * tracking, and the lock that stops them fighting during a smooth scroll.
 *
 * `activeSection` updates on every section crossing while scrolling, so call
 * this from a component that renders only the nav (see DetailSectionNav) —
 * not from the page — to keep those updates from re-rendering page content.
 *
 * `presentSectionIds` reports which of `sectionIds` the page actually rendered.
 *
 * `contentRef` is attached to the content container by the caller's parent.
 * It is read once on mount, which is safe because the nav and the content
 * mount in the same commit (both behind the page's query boundary), so the
 * ref is populated before effects run. Pass a stable `sectionIds` array so
 * the spy's listeners aren't torn down each render.
 */
export function useSectionNavigation(
  sectionIds: string[],
  contentRef: RefObject<HTMLDivElement | null>,
) {
  const { scrollTo, skipToContent } = useScrollToContent(contentRef);
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(
    null,
  );

  // Hand the element to the spy as state so its effect (re)runs once content
  // exists — a plain ref mutation wouldn't re-trigger it.
  useEffect(() => {
    setContentElement(contentRef.current);
  }, [contentRef]);

  const { activeSection, lockActiveSection, presentSectionIds } =
    useSectionScrollSpy({
      content: contentElement,
      sectionIds,
    });

  const onSelect = useCallback(
    (sectionId: string) => {
      // Only lock when a section actually scrolls; otherwise the spy would
      // freeze on a non-rendered id until the fallback release fires.
      if (scrollTo(sectionId)) {
        lockActiveSection(sectionId);
      }
    },
    [lockActiveSection, scrollTo],
  );

  return { activeSection, onSelect, skipToContent, presentSectionIds };
}
